import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import AdminPage from "./AdminPage";
import RssManagementPage from "./RssManagementPage";
import MonitoringPage from "./MonitoringPage";
import MainApp from "./MainApp";
import NewsAggregator from "./NewsAggregator";
import StoryDetailPage from "./StoryDetailPage";
import StorySwarms from "./StorySwarms";
import TestComparativeAnalysis from "./TestComparativeAnalysis";
import balanceAppTracking from "./BalanceAppTracking";
import {
  apiFetch,
  API_BASE,
  BACKEND_URL,
  AUTH_REQUEST_URL,
  AUTH_VERIFY_URL,
  AUTH_VALIDATE_URL,
  USAGE_URL,
} from "./config/apiConfig";
import "./App.css";

// Debug logging
// eslint-disable-next-line no-console
// Environment variables logged for debugging
// console.log("Environment variables:", {
//   REACT_APP_API_URL: process.env.REACT_APP_API_URL,
//   BACKEND_URL: BACKEND_URL,
// });

// Helper function to extract domain from URL
function extractDomain(url) {
  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObj.hostname;
  } catch (e) {
    return "Unknown Domain";
  }
}

function App() {
  const [urlInput, setUrlInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [biasInfo, setBiasInfo] = useState(null);
  const [articles, setArticles] = useState([]);

  // User-level filter toggles
  const [opposingViewpointsEnabled, setOpposingViewpointsEnabled] =
    useState(true); // Default enabled

  // Auth state
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(
    localStorage.getItem("sessionToken") || ""
  );
  const [authStep, setAuthStep] = useState("request"); // "request", "verify", "authenticated"
  const [authLoading, setAuthLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [message, setMessage] = useState(null);

  // Admin state - removed showAdminDashboard since we navigate directly

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");

  // Validate session on app load
  useEffect(() => {
    if (sessionToken) {
      validateSession();
    } else {
      // Check for session token or magic link token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const directSessionToken = urlParams.get("sessionToken");
      const magicLinkToken = urlParams.get("token");

      if (directSessionToken) {
        // Handle direct session token from authenticated URL
        setSessionToken(directSessionToken);
        localStorage.setItem("sessionToken", directSessionToken);
        validateSessionDirect(directSessionToken);
        // Clear URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } else if (magicLinkToken) {
        // Handle magic link token
        verifyMagicLink(magicLinkToken);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  // Load usage info when user changes
  useEffect(() => {
    if (user?.email) {
      loadUsageInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Periodic session validation (every 5 minutes)
  useEffect(() => {
    if (authStep === "authenticated" && sessionToken) {
      const intervalId = setInterval(
        () => {
          // eslint-disable-next-line no-console
          console.log("🔄 Performing periodic session validation...");
          validateSession();
        },
        5 * 60 * 1000
      ); // 5 minutes

      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStep, sessionToken]);

  const validateSession = async () => {
    if (!sessionToken) {
      // eslint-disable-next-line no-console
      console.log("🔐 No session token found, showing login screen");
      return;
    }

    // eslint-disable-next-line no-console
    console.log(
      "🔐 Validating session token:",
      sessionToken.substring(0, 8) + "..."
    );

    try {
      const response = await apiFetch("/auth/validate", {
        method: "POST",
        body: JSON.stringify({ sessionToken }),
      });

      const data = await response.json();
      // eslint-disable-next-line no-console
      console.log("🔐 Session validation response:", {
        status: response.status,
        valid: data.valid,
        user: data.user?.email,
        isAdmin: data.user?.isAdmin,
      });

      if (response.ok && data.valid) {
        // Preserve existing user state if response is missing critical data
        const currentUser = user;
        const newUser = data.user;

        // If response doesn't include admin status but current user has it, preserve it temporarily
        if (
          currentUser?.isAdmin &&
          newUser &&
          !newUser.hasOwnProperty("isAdmin")
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            "⚠️ Response missing isAdmin field, preserving current admin status"
          );
          newUser.isAdmin = currentUser.isAdmin;
        }

        setUser(newUser);
        setAuthStep("authenticated");
        // Expose user data globally for GTM admin detection
        window.user = newUser;
        localStorage.setItem("userData", JSON.stringify(newUser));

        // eslint-disable-next-line no-console
        console.log(
          "✅ Successfully authenticated:",
          newUser.email,
          "Admin:",
          newUser.isAdmin
        );

        // Start session tracking
        balanceAppTracking.startSession();
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          "❌ Session validation failed:",
          data.error || "Invalid session"
        );
        logout();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("❌ Session validation error:", err);
      logout();
    }
  };

  const validateSessionDirect = async (token) => {
    try {
      const response = await apiFetch("/auth/validate", {
        method: "POST",
        body: JSON.stringify({ sessionToken: token }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setUser(data.user);
        setAuthStep("authenticated");
        // Expose user data globally for GTM admin detection
        window.user = data.user;
        localStorage.setItem("userData", JSON.stringify(data.user));

        // Start session tracking
        balanceAppTracking.startSession();
      } else {
        logout();
      }
    } catch (err) {
      setError("Session validation failed");
      logout();
    }
  };

  const requestMagicLink = async (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim();

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setAuthLoading(true);
    setError(null);

    try {
      const response = await apiFetch("/authentication/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send magic link");
      }

      // Track magic link request
      balanceAppTracking.trackMagicLinkRequest(email);

      setMagicLinkSent(true);
      setError(null);

      // Removed development magic link display for production
    } catch (err) {
      setError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const verifyMagicLink = async (token) => {
    setAuthLoading(true);
    setError(null);

    try {
      const response = await apiFetch("/auth/verify", {
        method: "POST",
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Authentication failed");
      }

      setSessionToken(data.sessionToken);
      localStorage.setItem("sessionToken", data.sessionToken);
      setUser(data.user);
      setAuthStep("authenticated");
      setError(null);
      // Expose user data globally for GTM admin detection
      window.user = data.user;
      localStorage.setItem("userData", JSON.stringify(data.user));

      // Track successful authentication
      balanceAppTracking.trackAuthentication(data.user.email, true);

      // Check if this is a new user registration
      if (data.isNewUser) {
        balanceAppTracking.trackUserRegistration(data.user.email);
      }

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      setError(err.message);
      // Track failed authentication
      balanceAppTracking.trackAuthentication(null, false);
    } finally {
      setAuthLoading(false);
    }
  };

  const loadUsageInfo = async () => {
    if (!user?.email) return;

    try {
      const response = await apiFetch(
        `/usage?email=${encodeURIComponent(user.email)}`
      );
      const data = await response.json();

      if (response.ok) {
        setUsageInfo(data);
      }
    } catch (err) {
      // Silently fail for usage info
    }
  };

  const logout = () => {
    // End session tracking
    balanceAppTracking.endSession();

    setUser(null);
    setSessionToken("");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("userData");
    window.user = null;
    setAuthStep("request");
    setMagicLinkSent(false);
    setUsageInfo(null);
    setBiasInfo(null);
    setArticles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!urlInput.trim()) {
      setError("Please enter a valid URL");
      return;
    }

    setLoading(true);
    setError(null);
    setBiasInfo(null);
    setArticles([]);

    try {
      const response = await apiFetch("/bias-check", {
        method: "POST",
        body: JSON.stringify({
          url: urlInput.trim(),
          opposing_viewpoints_filter: opposingViewpointsEnabled,
          ...(user?.email && { email: user.email }),
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const processedBiasScore =
        data.bias_score_numeric !== null &&
        data.bias_score_numeric !== undefined
          ? data.bias_score_numeric
          : "Unknown";
      const processedCredibilityScore =
        data.credibility_score_numeric !== null &&
        data.credibility_score_numeric !== undefined
          ? data.credibility_score_numeric
          : "Unknown";

      // Extract domain from URL if source is not provided
      const extractedDomain = data.source || extractDomain(urlInput.trim());

      setBiasInfo({
        bias_score: processedBiasScore,
        bias_source: data.bias_source,
        credibility_score: processedCredibilityScore,
        credibility_source: data.credibility_source,
        source: extractedDomain,
        url: urlInput.trim(),
        analysis_date: new Date().toISOString(),
        comparative_analysis: data.comparative_analysis,
        reasoning: data.reasoning,
        suggested_articles: data.suggested_articles || [],
      });

      // Set articles from suggested articles
      if (data.suggested_articles && data.suggested_articles.length > 0) {
        setArticles(data.suggested_articles);
      }

      // Track bias check
      balanceAppTracking.trackBiasCheck(
        urlInput.trim(),
        extractedDomain,
        processedBiasScore,
        processedCredibilityScore,
        user?.email
      );
    } catch (err) {
      setError(err.message);
      // Track failed bias check
      balanceAppTracking.trackBiasCheckError(urlInput.trim(), err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderBiasInfo = () => {
    if (!biasInfo) return null;

    // Check if this is search mode
    const isSearchMode = biasInfo.search_mode;

    // Don't show Analysis Results box for search mode
    if (isSearchMode) {
      return null;
    }

    return (
      <div className="space-y-6 fade-in" data-testid="bias-results">
        {/* Main Analysis Card */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-white">
              Analysis Results
            </h2>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bias Score */}
                <div className="text-center p-4 bg-neutral-50 rounded-lg">
                  <h3 className="text-sm font-medium text-neutral-600 mb-2">
                    Political Bias
                  </h3>
                  <div
                    className={`text-lg font-semibold mb-2 ${getBiasClass(biasInfo.bias_score)}`}
                  >
                    {formatBiasScore(biasInfo.bias_score)}
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        biasInfo.bias_score < -1
                          ? "bg-blue-500"
                          : biasInfo.bias_score > 1
                            ? "bg-red-500"
                            : "bg-green-500"
                      }`}
                      style={{
                        width: `${Math.min(Math.abs(biasInfo.bias_score) * 50 + 10, 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Credibility Score */}
                <div className="text-center p-4 bg-neutral-50 rounded-lg">
                  <h3 className="text-sm font-medium text-neutral-600 mb-2">
                    Credibility
                  </h3>
                  <div
                    className={`text-lg font-semibold mb-2 ${getCredibilityClass(biasInfo.credibility_score)}`}
                  >
                    {formatCredibilityScore(biasInfo.credibility_score)}
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(biasInfo.credibility_score * 100, 10)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* AI Analysis */}
              {biasInfo.ai_analysis &&
                (() => {
                  // Parse the AI analysis to extract summary and bias analysis
                  const analysis = biasInfo.ai_analysis;
                  const summaryMatch = analysis.match(
                    /Summary:\s*(.*?)(?:\s*Bias Analysis:|$)/s
                  );
                  const biasMatch = analysis.match(/Bias Analysis:\s*(.*)/s);

                  const summary = summaryMatch ? summaryMatch[1].trim() : "";
                  const biasAnalysis = biasMatch ? biasMatch[1].trim() : "";

                  return (
                    <div className="space-y-4">
                      {/* Article Summary */}
                      {summary && (
                        <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Article Summary
                          </h3>
                          <p className="text-blue-800 text-sm leading-relaxed">
                            {summary}
                          </p>
                        </div>
                      )}

                      {/* Bias Analysis */}
                      {biasAnalysis && (
                        <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                          <h3 className="text-sm font-semibold text-purple-900 mb-3 flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Bias Analysis
                          </h3>
                          <p className="text-purple-800 text-sm leading-relaxed">
                            {biasAnalysis}
                          </p>
                        </div>
                      )}

                      {/* Fallback if parsing fails */}
                      {!summary && !biasAnalysis && (
                        <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                          <h3 className="text-sm font-semibold text-purple-900 mb-3 flex items-center">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            AI Analysis
                          </h3>
                          <p className="text-purple-800 text-sm leading-relaxed">
                            {analysis}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* Comparative Analysis */}
              {biasInfo.comparative_analysis &&
                (() => {
                  const analysis = biasInfo.comparative_analysis.analysis;

                  // Parse the comparative analysis into perspective sections
                  const parseComparativeAnalysis = (text) => {
                    const sections = [];
                    const lines = text.split("\n");
                    let currentSection = null;

                    for (const line of lines) {
                      const trimmedLine = line.trim();
                      if (!trimmedLine) continue;

                      // Handle format: "LEFT:" or "Left:"
                      if (
                        trimmedLine.match(
                          /^(LEFT|CENTER|RIGHT|Left|Center|Right):/i
                        )
                      ) {
                        if (currentSection) sections.push(currentSection);

                        const [perspective, ...contentParts] =
                          trimmedLine.split(":");
                        currentSection = {
                          perspective: perspective.trim().toUpperCase(),
                          points:
                            contentParts.length > 0
                              ? [contentParts.join(":").trim()]
                              : [],
                        };
                      }
                      // Handle format: "- Left-leaning sources..." or "- Center/neutral sources..."
                      else if (
                        trimmedLine.match(
                          /^-\s*(Left-leaning|Center\/neutral|Right-leaning|Left|Center|Right)/i
                        )
                      ) {
                        if (currentSection) sections.push(currentSection);

                        let perspective = "CENTER";
                        if (trimmedLine.match(/Left-leaning|Left/i)) {
                          perspective = "LEFT";
                        } else if (trimmedLine.match(/Right-leaning|Right/i)) {
                          perspective = "RIGHT";
                        }

                        // Extract content after the perspective identifier
                        const content = trimmedLine
                          .replace(
                            /^-\s*(Left-leaning|Center\/neutral|Right-leaning)\s*sources?\s*/i,
                            ""
                          )
                          .trim();

                        currentSection = {
                          perspective: perspective,
                          points: content ? [content] : [],
                        };
                      }
                      // Handle regular bullet points
                      else if (trimmedLine.startsWith("-") && currentSection) {
                        currentSection.points.push(
                          trimmedLine.substring(1).trim()
                        );
                      } else if (
                        currentSection &&
                        currentSection.points.length === 0
                      ) {
                        // First line after perspective header
                        currentSection.points.push(trimmedLine);
                      } else if (currentSection) {
                        // Continue previous point
                        const lastIndex = currentSection.points.length - 1;
                        if (lastIndex >= 0) {
                          currentSection.points[lastIndex] += " " + trimmedLine;
                        }
                      }
                    }

                    if (currentSection) sections.push(currentSection);
                    return sections;
                  };

                  const sections = parseComparativeAnalysis(analysis);

                  const getPerspectiveColor = (perspective) => {
                    switch (perspective) {
                      case "LEFT":
                        return {
                          bg: "bg-blue-50",
                          border: "border-blue-400",
                          text: "text-blue-900",
                          icon: "←",
                        };
                      case "CENTER":
                        return {
                          bg: "bg-green-50",
                          border: "border-green-400",
                          text: "text-green-900",
                          icon: "⚖️",
                        };
                      case "RIGHT":
                        return {
                          bg: "bg-red-50",
                          border: "border-red-400",
                          text: "text-red-900",
                          icon: "→",
                        };
                      default:
                        return {
                          bg: "bg-gray-50",
                          border: "border-gray-400",
                          text: "text-gray-900",
                          icon: "•",
                        };
                    }
                  };

                  return (
                    <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
                      <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-neutral-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        Media bias on this story
                      </h3>
                      <div className="space-y-4">
                        {sections.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Ensure we have all three perspectives in the right order */}
                            {["LEFT", "CENTER", "RIGHT"].map((perspective) => {
                              const section = sections.find(
                                (s) => s.perspective === perspective
                              );
                              const colors = getPerspectiveColor(perspective);

                              return (
                                <div
                                  key={perspective}
                                  className={`p-4 ${colors.bg} rounded-lg border-l-4 ${colors.border} min-h-[200px]`}
                                >
                                  <h4
                                    className={`text-sm font-semibold ${colors.text} mb-3 flex items-center justify-center text-center`}
                                  >
                                    <span className="mr-2 text-lg">
                                      {colors.icon}
                                    </span>
                                    {perspective}-LEANING
                                  </h4>
                                  <div className="space-y-2">
                                    {section ? (
                                      section.points.map(
                                        (point, pointIndex) => (
                                          <div
                                            key={pointIndex}
                                            className={`${colors.text.replace("900", "800")} text-sm leading-relaxed`}
                                          >
                                            {point}
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <div
                                        className={`${colors.text.replace("900", "600")} text-sm italic text-center`}
                                      >
                                        No {perspective.toLowerCase()}-leaning
                                        analysis available
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // Fallback if parsing fails
                          <div className="p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-400">
                            <h3 className="text-sm font-semibold text-emerald-900 mb-3 flex items-center">
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Comparative Analysis
                            </h3>
                            <div className="text-emerald-800 text-sm leading-relaxed whitespace-pre-wrap">
                              {analysis}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* Topics */}
              {biasInfo.topics && biasInfo.topics.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h3 className="text-sm font-medium text-amber-900 mb-2">
                    Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {biasInfo.topics.map((topic, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatBiasScore = (score) => {
    if (score === "Unknown" || score === null || score === undefined) {
      return "Unknown";
    }

    const numScore = parseFloat(score);
    if (isNaN(numScore)) return "Unknown";

    if (numScore <= -1) return "Left";
    if (numScore <= -0.25) return "Lean Left";
    if (numScore >= 1) return "Right";
    if (numScore >= 0.25) return "Lean Right";
    return "Center/Neutral";
  };

  const formatCredibilityScore = (score) => {
    if (score === "Unknown" || score === null || score === undefined) {
      return "Unknown";
    }

    const numScore = parseFloat(score);
    if (isNaN(numScore)) return "Unknown";

    if (numScore >= 0.8) return "Excellent";
    if (numScore >= 0.6) return "Good";
    if (numScore >= 0.4) return "Fair";
    return "Poor";
  };

  const getBiasClass = (score) => {
    if (score === "Unknown") return "text-neutral-500";

    const numScore = parseFloat(score);
    if (isNaN(numScore)) return "text-neutral-500";

    if (numScore <= -0.25) return "text-blue-600";
    if (numScore >= 0.25) return "text-red-600";
    return "text-green-600";
  };

  const getCredibilityClass = (score) => {
    if (score === "Unknown") return "text-neutral-500";

    const numScore = parseFloat(score);
    if (isNaN(numScore)) return "text-neutral-500";

    if (numScore >= 0.8) return "text-emerald-600";
    if (numScore >= 0.6) return "text-yellow-600";
    return "text-red-600";
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Unknown";

      // Format as "Jan 15, 2024"
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Unknown";
    }
  };

  // Get relative time (e.g., "2 days ago")
  const getRelativeTime = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

      if (diffInHours < 1) return "Less than an hour ago";
      if (diffInHours < 24) return `${diffInHours} hours ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) return "1 day ago";
      if (diffInDays < 7) return `${diffInDays} days ago`;

      const diffInWeeks = Math.floor(diffInDays / 7);
      if (diffInWeeks === 1) return "1 week ago";
      if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;

      return formatDate(dateString);
    } catch (error) {
      return "";
    }
  };

  // Sort articles
  const sortArticles = (articlesToSort, field, direction) => {
    if (!field) return articlesToSort;

    return [...articlesToSort].sort((a, b) => {
      let aValue, bValue;

      switch (field) {
        case "bias":
          aValue = typeof a.bias_score === "number" ? a.bias_score : -999;
          bValue = typeof b.bias_score === "number" ? b.bias_score : -999;
          break;
        case "credibility":
          aValue =
            typeof a.credibility_score === "number" ? a.credibility_score : -1;
          bValue =
            typeof b.credibility_score === "number" ? b.credibility_score : -1;
          break;
        case "relevance":
          aValue =
            typeof a.relevance_score === "number" ? a.relevance_score : -1;
          bValue =
            typeof b.relevance_score === "number" ? b.relevance_score : -1;
          break;
        case "date":
          aValue = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
          bValue = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (direction === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  // Handle sort change
  const handleSort = (field) => {
    let newDirection;
    if (sortField === field) {
      // Toggle direction if same field
      newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      // Set new field with default desc direction
      setSortField(field);
      newDirection = "desc";
      setSortDirection(newDirection);
    }

    // Track article sorting behavior
    balanceAppTracking.trackArticleSort(field, newDirection);
  };

  const handleAnalyseBias = async (article) => {
    if (!article.url) {
      setError("No URL available for this article");
      return;
    }

    setLoading(true);
    setError(null);
    setBiasInfo(null);
    setArticles([]);

    try {
      const response = await apiFetch("/bias-check", {
        method: "POST",
        body: JSON.stringify({
          url: article.url.trim(),
          text: `Title: ${article.title}\nSource: ${article.source}\nDescription: ${article.description || ""}`,
          opposing_viewpoints_filter: opposingViewpointsEnabled,
          ...(user?.email && { email: user.email }),
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const processedBiasScore =
        data.bias_score_numeric !== null &&
        data.bias_score_numeric !== undefined
          ? data.bias_score_numeric
          : "Unknown";
      const processedCredibilityScore =
        data.credibility_score_numeric !== null &&
        data.credibility_score_numeric !== undefined
          ? data.credibility_score_numeric
          : "Unknown";

      // Extract domain from URL if source is not provided
      const extractedDomain = data.source || extractDomain(article.url.trim());

      setBiasInfo({
        bias_score: processedBiasScore,
        bias_source: data.bias_source,
        credibility_score: processedCredibilityScore,
        credibility_source: data.credibility_source,
        ai_analysis: data.ai_analysis,
        comparative_analysis: data.comparative_analysis,
        topics: data.topics,
        source: extractedDomain,
        search_mode: data.search_mode,
        domain: extractedDomain,
      });

      // Track the secondary analysis
      balanceAppTracking.trackArticleAnalysis(
        article.url.trim(),
        processedBiasScore,
        processedCredibilityScore,
        "secondary_article"
      );

      // Process suggested articles with proper bias/credibility mapping
      if (Array.isArray(data.suggested_articles)) {
        setArticles(
          data.suggested_articles.map((suggestedArticle, idx) => {
            const processedArticle = {
              id: idx,
              title: suggestedArticle.title || "Untitled",
              url: suggestedArticle.url || "#",
              source:
                typeof suggestedArticle.source === "object"
                  ? suggestedArticle.source?.name || "Unknown"
                  : suggestedArticle.source || "Unknown",
              bias_score:
                suggestedArticle.bias_score_numeric !== null &&
                suggestedArticle.bias_score_numeric !== undefined
                  ? suggestedArticle.bias_score_numeric
                  : "Unknown",
              credibility_score:
                suggestedArticle.credibility_score_numeric !== null &&
                suggestedArticle.credibility_score_numeric !== undefined
                  ? suggestedArticle.credibility_score_numeric
                  : "Unknown",
              relevance_score: suggestedArticle.relevance_score || null,
              relevance_percentage:
                suggestedArticle.relevance_percentage || null,
              publishedAt: suggestedArticle.publishedAt || null,
            };
            return processedArticle;
          })
        );
      } else {
        setArticles([]);
      }

      // Update the search input to show the resolved article URL from backend
      setUrlInput(data.resolved_url || article.url.trim());

      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.querySelector(
          '[data-testid="bias-results"]'
        );
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error analyzing bias:", error);
      setError(error.message || "Failed to analyze bias. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderSuggestedArticles = () => {
    if (!articles || articles.length === 0) return null;

    // Sort articles based on current sort settings
    const sortedArticles = sortArticles(articles, sortField, sortDirection);

    return (
      <div className="space-y-4 fade-in">
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-white">
              Related Articles
            </h2>
            <p className="text-emerald-100 text-sm">
              Discover diverse perspectives on this topic
            </p>
          </div>

          {/* Sorting Controls */}
          <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-neutral-700 mr-2">
                Sort by:
              </span>

              <button
                onClick={() => handleSort("date")}
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  sortField === "date"
                    ? "bg-primary-100 text-primary-800 border border-primary-200"
                    : "bg-white text-neutral-600 border border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                Date
                {sortField === "date" && (
                  <svg
                    className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </button>

              <button
                onClick={() => handleSort("bias")}
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  sortField === "bias"
                    ? "bg-primary-100 text-primary-800 border border-primary-200"
                    : "bg-white text-neutral-600 border border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                Bias
                {sortField === "bias" && (
                  <svg
                    className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </button>

              <button
                onClick={() => handleSort("credibility")}
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  sortField === "credibility"
                    ? "bg-primary-100 text-primary-800 border border-primary-200"
                    : "bg-white text-neutral-600 border border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                Credibility
                {sortField === "credibility" && (
                  <svg
                    className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </button>

              <button
                onClick={() => handleSort("relevance")}
                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  sortField === "relevance"
                    ? "bg-primary-100 text-primary-800 border border-primary-200"
                    : "bg-white text-neutral-600 border border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                Relevance
                {sortField === "relevance" && (
                  <svg
                    className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </button>

              {sortField && (
                <button
                  onClick={() => setSortField("")}
                  className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-red-100 text-red-800 border border-red-200 hover:bg-red-50 transition-colors"
                >
                  Clear Sort
                  <svg
                    className="ml-1 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Articles Table */}
          <div className="overflow-x-auto">
            <div className="divide-y divide-neutral-200">
              {sortedArticles.map((article, index) => (
                <div
                  key={article.id || index}
                  className="p-6 hover:bg-neutral-50 transition-colors duration-200"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-start">
                    {/* Article Title and Source - Takes most space */}
                    <div className="lg:col-span-5 min-w-0">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          balanceAppTracking.trackArticleClick(
                            article.url,
                            article.bias_score,
                            article.credibility_score,
                            index
                          )
                        }
                        className="text-primary-600 hover:text-primary-800 font-medium text-lg leading-6 block hover:underline transition-colors duration-200"
                      >
                        {article.title}
                      </a>
                      <p className="text-neutral-600 text-sm mt-1">
                        <span className="font-medium">Source:</span>{" "}
                        {article.source}
                      </p>
                    </div>

                    {/* Date Column */}
                    <div className="lg:col-span-2">
                      <div className="text-sm">
                        <div className="font-medium text-neutral-900">
                          {formatDate(article.publishedAt)}
                        </div>
                        <div className="text-neutral-500 text-xs">
                          {getRelativeTime(article.publishedAt)}
                        </div>
                      </div>
                    </div>

                    {/* Scores Column */}
                    <div className="lg:col-span-3">
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getBiasClass(article.bias_score) === "text-blue-600"
                              ? "bg-blue-100 text-blue-800"
                              : getBiasClass(article.bias_score) ===
                                  "text-red-600"
                                ? "bg-red-100 text-red-800"
                                : getBiasClass(article.bias_score) ===
                                    "text-green-600"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-neutral-100 text-neutral-800"
                          }`}
                        >
                          {formatBiasScore(article.bias_score)}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getCredibilityClass(article.credibility_score) ===
                            "text-emerald-600"
                              ? "bg-emerald-100 text-emerald-800"
                              : getCredibilityClass(
                                    article.credibility_score
                                  ) === "text-yellow-600"
                                ? "bg-yellow-100 text-yellow-800"
                                : getCredibilityClass(
                                      article.credibility_score
                                    ) === "text-red-600"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-neutral-100 text-neutral-800"
                          }`}
                        >
                          {formatCredibilityScore(article.credibility_score)}
                        </span>
                        {/* Relevance Score Badge */}
                        {article.relevance_percentage && (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              article.relevance_score >= 0.8
                                ? "bg-purple-100 text-purple-800"
                                : article.relevance_score >= 0.6
                                  ? "bg-indigo-100 text-indigo-800"
                                  : article.relevance_score >= 0.4
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-neutral-100 text-neutral-800"
                            }`}
                            title="Semantic relevance to original article"
                          >
                            🎯 {article.relevance_percentage}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Analyse Bias Button Column */}
                    <div className="lg:col-span-2">
                      <button
                        onClick={() => handleAnalyseBias(article)}
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-primary-300 text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        title="Analyse bias for this article"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full mr-2"></div>
                            <span className="text-xs">Analyzing...</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-4 w-4 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"
                              />
                            </svg>
                            <span className="text-xs">Analyse Bias</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMagicLinkRequest = () => (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img
              src="/logo-full.png"
              alt="Not My View Logo"
              style={{ height: "80px", width: "auto", maxHeight: "80px" }}
            />
          </div>
          <p className="mt-2 text-neutral-600">
            Enter your email to receive a secure sign-in link
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={requestMagicLink} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Email address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                className="input-field"
                placeholder="Enter your email address"
                required
                disabled={authLoading}
              />
            </div>
            <button
              type="submit"
              disabled={authLoading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {authLoading ? (
                <>
                  <div className="spinner"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 7.89a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
                    />
                  </svg>
                  <span>Send Magic Link</span>
                </>
              )}
            </button>
          </form>

          {magicLinkSent && (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg fade-in">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-emerald-800">
                    Magic link sent!
                  </h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    Check your email and click the link to sign in.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderUserInfo = () => {
    if (!user || !user.email) return null;

    return (
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {user.email.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">
                {user.email}
              </p>
              <div className="flex items-center space-x-2">
                {user.isAdmin && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Admin
                  </span>
                )}
                {usageInfo && (
                  <span className="text-xs text-neutral-500">
                    {usageInfo.remaining === "unlimited"
                      ? "Unlimited searches"
                      : `${usageInfo.remaining} searches remaining`}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                window.location.href = "/news";
              }}
              className="btn-secondary text-sm"
            >
              <svg
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3v8m0 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2h2a2 2 0 002-2zm0 4h.01"
                />
              </svg>
              News Aggregator
            </button>
            {user.isAdmin && (
              <button
                onClick={() => {
                  window.location.href = "/admin";
                }}
                className="btn-secondary text-sm"
              >
                <svg
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"
                  />
                </svg>
                Admin Dashboard
              </button>
            )}
            <button
              onClick={logout}
              className="btn-secondary text-sm text-red-700 hover:text-red-800 hover:bg-red-50"
            >
              <svg
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show authentication screen if not authenticated
  if (authStep !== "authenticated") {
    return (
      <div className="min-h-screen bg-neutral-50">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        {renderMagicLinkRequest()}
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <MainApp
              user={user}
              error={error}
              setError={setError}
              urlInput={urlInput}
              setUrlInput={setUrlInput}
              loading={loading}
              opposingViewpointsEnabled={opposingViewpointsEnabled}
              setOpposingViewpointsEnabled={setOpposingViewpointsEnabled}
              handleSubmit={handleSubmit}
              renderBiasInfo={renderBiasInfo}
              renderSuggestedArticles={renderSuggestedArticles}
              message={message}
              setMessage={setMessage}
              renderUserInfo={renderUserInfo}
            />
          }
        />
        <Route path="/auth/verify" element={<Navigate to="/" replace />} />
        <Route
          path="/admin"
          element={
            user?.isAdmin ? (
              <AdminPage user={user} logout={logout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/rss"
          element={
            user?.isAdmin ? (
              <RssManagementPage user={user} logout={logout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/monitoring"
          element={
            user?.isAdmin ? (
              <MonitoringPage user={user} logout={logout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/news"
          element={
            <NewsAggregator
              user={user}
              logout={logout}
              renderUserInfo={renderUserInfo}
            />
          }
        />
        <Route
          path="/story/:storyId"
          element={
            <StoryDetailPage
              user={user}
              logout={logout}
              renderUserInfo={renderUserInfo}
            />
          }
        />
        <Route
          path="/story-swarms"
          element={
            <StorySwarms
              user={user}
              logout={logout}
              renderUserInfo={renderUserInfo}
            />
          }
        />
        <Route
          path="/test-comparative-analysis"
          element={
            <TestComparativeAnalysis
              user={user}
              logout={logout}
              renderUserInfo={renderUserInfo}
            />
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
